/**
 * The React binding for src/ui/routing.ts. Kept separate so the route table
 * itself stays free of React and of `window`, and can be unit-tested directly.
 */

import { useMemo, useSyncExternalStore } from 'react';

import { hrefFor, parseRoute, type Route } from './routing';

function subscribe(onChange: () => void): () => void {
  window.addEventListener('hashchange', onChange);
  return () => window.removeEventListener('hashchange', onChange);
}

/**
 * The snapshot is the raw hash STRING, not a parsed Route. `useSyncExternalStore`
 * compares snapshots with Object.is, and `parseRoute` returns a fresh object
 * every call — returning one here would re-render forever.
 */
function readHash(): string {
  return window.location.hash;
}

export function useRoute(): Route {
  const hash = useSyncExternalStore(subscribe, readHash, () => '#/');
  return useMemo(() => parseRoute(hash), [hash]);
}

export interface NavigateOptions {
  /** Replace the current entry instead of pushing — used for redirects. */
  replace?: boolean;
}

export function navigate(route: Route, options: NavigateOptions = {}): void {
  if (typeof window === 'undefined') return;
  const href = hrefFor(route);
  if (options.replace) {
    window.history.replaceState(null, '', href);
    // replaceState does not fire hashchange; the subscribers still need telling.
    window.dispatchEvent(new Event('hashchange'));
    return;
  }
  if (window.location.hash === href) return;
  window.location.hash = href.slice(1);
}

/** Props for an anchor that navigates. Anchors, so long-press / open-in-new works. */
export function linkProps(route: Route): { href: string } {
  return { href: hrefFor(route) };
}
